import { hasPermission, visibleSections } from "@xop/dashboard-shared";
import { Navigate } from "react-router-dom";

import { DashboardPage } from "../pages/DashboardPage.js";
import { pathFor } from "../components/AppShell.js";
import { useAuth } from "./AuthProvider.js";

/**
 * The app's index (`/`) landing.
 *
 * Not every role has the company-pulse dashboard. A sales role
 * (`sales_manager`) works entirely inside CRM / Sales / Marketing, so it
 * ships with **no `dashboard:view`** — and the cross-company pulse would
 * just be noise. Rather than render a Forbidden page at `/` for those
 * users, send them straight to their first usable section.
 *
 *   - `dashboard:view` present  -> the Company-pulse dashboard, as before.
 *   - `dashboard:view` absent   -> redirect to the FIRST section the user
 *                                  can see (`visibleSections` preserves the
 *                                  nav order, so this is CRM for a sales
 *                                  role). The matching nav tab is already
 *                                  hidden by the same permission, so the two
 *                                  stay in lockstep.
 *   - no visible section at all -> fall back to the always-reachable
 *                                  `/profile` (no signed-in user should ever
 *                                  hit a dead `/`).
 *
 * The outer `RequireAuth` on the layout route guarantees `user` is present
 * here, so this only ever decides *which* authorized surface to show.
 */
export function LandingRoute(): JSX.Element {
  const { user } = useAuth();
  const permissions = user?.permissions;

  if (hasPermission(permissions, "dashboard:view")) {
    return <DashboardPage />;
  }

  const first = visibleSections(permissions)[0];
  return <Navigate to={first ? pathFor(first) : "/profile"} replace />;
}
